import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, LifeBuoy, Mail, Clock3 } from 'lucide-react'
import logoLight from '../assets/xtract-symbol-light.png'
import logoDark from '../assets/xtract-symbol-dark.png'

/**
 * Public Support page - accessible without authentication.
 * Used as the "Support website" URL on the Microsoft Marketplace / Partner
 * Center offer listing, so prospective customers (who aren't signed in to
 * any Xtract deployment yet) can see how to get help.
 *
 * Only uses verified, real contact details (info@synapx.com - the same
 * address already published on the Privacy Policy page). Do not add any
 * other email/domain here without first confirming Synapx actually owns it.
 */
function PublicSupport() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="px-8 lg:px-16 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoLight} alt="Xtract" className="w-8 h-8 rounded-lg dark:hidden" />
            <img src={logoDark} alt="Xtract" className="hidden dark:block w-8 h-8 rounded-lg" />
            <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">Xtract</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">by Synapx AI</span>
          </div>
          <Link
            to="/login"
            className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="px-8 lg:px-16 py-12 max-w-3xl mx-auto">
        <div className="flex items-center gap-3.5 mb-8">
          <div className="hidden sm:flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10">
            <LifeBuoy className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Support</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Get help with Xtract - for prospective and current customers.
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-lg bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300 flex items-center justify-center flex-shrink-0">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Contact Support</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                For technical support, account questions, or general enquiries, email{' '}
                <a href="mailto:info@synapx.com" className="text-navy-700 dark:text-navy-400 underline">info@synapx.com</a>.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">What to include in a support request</h2>
          <ul className="list-disc list-inside space-y-1.5 text-sm text-gray-600 dark:text-gray-300">
            <li>A short summary of the issue and when it started</li>
            <li>Your organisation name</li>
            <li>Expected behaviour versus current behaviour</li>
            <li>Screenshots and the approximate time the issue occurred</li>
          </ul>
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">Typical response times</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5 bg-gray-50/70 dark:bg-gray-800/40">
              <span className="text-sm text-gray-600 dark:text-gray-300">Critical outage</span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 dark:text-red-300"><Clock3 className="h-3.5 w-3.5" /> &lt; 4h</span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5 bg-gray-50/70 dark:bg-gray-800/40">
              <span className="text-sm text-gray-600 dark:text-gray-300">Workflow blocked</span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 dark:text-amber-300"><Clock3 className="h-3.5 w-3.5" /> same day</span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5 bg-gray-50/70 dark:bg-gray-800/40">
              <span className="text-sm text-gray-600 dark:text-gray-300">General queries</span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-navy-700 dark:text-navy-300"><Clock3 className="h-3.5 w-3.5" /> 1-2 days</span>
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Response windows are indicative and may vary by deployment complexity.</p>
        </div>

        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-8">
          © {new Date().getFullYear()} Xtract by Synapx Ltd. All rights reserved.
        </p>
      </main>
    </div>
  )
}

export default PublicSupport
